import { createSlice } from '@reduxjs/toolkit';
import { aesEncrypt } from '../Utils/Common';

const initialState = {
    user: null,
    allUsers: [],
    conversations: [],
    conversationMap: null,
    active_conversation: null,
    archive_conversations: [],
    msgs: [],
    reply_msgs: [],
    reply_main_msg: null,
    active_file: null,
    active_msg: null,
    active_link: null,
    delete_msg: null,
    delete_file: null,
    userList: { key: '', state: false },
    roomProperty: { key: '', state: false },
    mute_popup: { key: '', state: false },
    title_popup: { key: '', state: false },
    tag_popup: false,
    user_profile: false,
    editMsg_popup: false,
    linkShare_popup: false,
    total_unread: null,
    chat_loader: true,
    msg_drawer: false,
    upload_popup: false,
    private_msg: false,
    file_drawer: false,
    raw_message_text: '',
    aiCompletedFiles: [],
    kick_out_data: null,
    online_users: [],
    deleted_msg: null,
    change_file_data: null,
    change_link_data: null,
    merge_msg: { msg: '' },
    connect_search: '',
    downloading_file: [],
    downloading_progress: {},
    voice_file: null,
    callringpopup: { status: false },
    conv_calling_data: [],
    pinAccess: [],
    updateVersion_data: 'no',
};

const messageSlice = createSlice({
    name: 'message',
    initialState,
    reducers: {
        setPopup: (state, action) => {
            state[action.payload.key] = action.payload.value;
        },
        setUser: (state, action) => {
            state.user = action.payload;
        },
        update_user_erp: (state, action) => {
            return ({
                ...state,
                user: { ...state.user, erpNext: action.payload.erpNext, erpNextRoles: action.payload.erpNextRoles }
            })
        },
        setAllUsers: (state, action) => {
            state.allUsers = action.payload;
        },
        setConversations: (state, action) => {
            state.conversations = action.payload;
        },
        add_conversations: (state, action) => {
            state.conversations = [action.payload, ...state.conversations]
        },
        setConversationMap: (state, action) => {
            state.conversationMap = action.payload;
        },
        updateConversationMap: (state, action) => {
            if (!state.conversationMap) {
                state.conversationMap = {};
            }
            const { key, value } = action.payload;
            state.conversationMap[key] = value;
        },
        updateConversationMapKey: (state, action) => {
            if (!state.conversationMap) {
                state.conversationMap = {};
            }

            const { oldKey, newKey } = action.payload;

            // Only rename if oldKey exists and newKey is different
            if (oldKey !== newKey && state.conversationMap.hasOwnProperty(oldKey)) {
                state.conversationMap[newKey] = state.conversationMap[oldKey];
                delete state.conversationMap[oldKey];
            }
        },

        setActiveConversation: (state, action) => {
            // debugger
            state.active_conversation = action.payload;
        },
        update_room_data: (state, action) => {
            if (state.active_conversation.conversation_id === action.payload.conversation_id) {
                return ({
                    ...state,
                    active_conversation: { ...action.payload },
                    conversations: state.conversations.map(v => v.conversation_id === action.payload.conversation_id ? { ...action.payload } : v)
                })
            } else {
                return ({
                    ...state,
                    conversations: state.conversations.map(v => v.conversation_id === action.payload.conversation_id ? { ...action.payload } : v)
                })
            }
        },
        set_archive_conversations: (state, action) => {
            return ({
                ...state,
                archive_conversations: [...action.payload]
            })
        },
        set_reply_main_msg: (state, action) => {
            return ({
                ...state,
                reply_main_msg: action.payload
            })
        },
        set_messages: (state, action) => {
            const old_msg_ids = state.msgs.map(v => v.msg_id);

            if (action.payload.is_reply_msg === 'no') {
                if (!action.payload.new) {
                    if (action.payload.page > 1) {
                        return {
                            ...state,
                            msgs: [
                                ...action.payload.data?.filter(v => !old_msg_ids.includes(v.msg_id)),
                                ...state.msgs,
                            ],
                        };
                    } else {
                        return {
                            ...state,
                            msgs: action.payload.data,
                        };
                    }
                }

                if (action.payload.my_new_msg) {
                    return {
                        ...state,
                        conversations: state.conversations
                            .map((c) =>
                                c.conversation_id === state.active_conversation.conversation_id
                                    ? { ...c, last_msg_time: action.payload.created_at }
                                    : c
                            )
                            .sort((a, b) => (a.last_msg_time > b.last_msg_time ? -1 : 1)),
                    };
                }
            } else {
                if (!action.payload.new) {
                    if (action.payload.page > 1) {
                        return {
                            ...state,
                            reply_msgs: [...action.payload.data, ...state.reply_msgs],
                        };
                    } else {
                        return {
                            ...state,
                            reply_msgs: action.payload.data,
                        };
                    }
                }
            }

            return state; // Default case to prevent returning undefined
        },
        update_messages: (state, action) => {
            const { conversation_id, msg_id, fields, is_reply_msg } = action.payload;

            if (is_reply_msg === 'no') {
                const messageToUpdate = state.msgs.find(msg => msg.conversation_id === conversation_id && msg.msg_id === msg_id);
                if (messageToUpdate) {
                    Object.assign(messageToUpdate, fields);
                }
            } else {
                const replyMessageToUpdate = state.reply_msgs.find(msg => msg.conversation_id === conversation_id && msg.msg_id === msg_id);
                if (replyMessageToUpdate) {
                    Object.assign(replyMessageToUpdate, fields);
                }
            }
        },
        update_msg_link: (state, action) => {
            const { msg_id, link_data, is_reply_msg } = action.payload;
            return ({
                ...state,
                reply_msgs: is_reply_msg === 'yes' ? state.reply_msgs.map((msg) => msg.msg_id === msg_id ? { ...msg, link_data } : msg) : state.reply_msgs,
                msgs: is_reply_msg === 'no' ? state.msgs.map((msg) => msg.msg_id === msg_id ? { ...msg, link_data } : msg) : state.msgs
            })
        },
        removeMessage: (state, action) => {
            const { is_reply_msg } = action.payload;
            state.msgs = is_reply_msg === 'no' ? state.msgs?.filter(v => v.msg_id !== action.payload.msg_id) : state.msgs;
            state.reply_msgs = is_reply_msg === 'yes' ? state.reply_msgs?.filter(v => v.msg_id !== action.payload.msg_id) : state.reply_msgs;
        },
        set_pin_unpin: (state, action) => {
            state.active_conversation = { ...state.active_conversation, pin: (action.payload.action === 'pin' ? [state.user?.id] : []) };
            state.conversations = state.conversations.map(m => m.conversation_id === action.payload.conversation_id ? { ...m, pin: action.payload.action === 'pin' ? [state.user?.id] : [] } : m);
        },
        updateArchiveConv: (state, action) => {
            if (state.active_conversation) {
                state.active_conversation = {
                    ...state.active_conversation,
                    archive: action.payload.conversation_id === state.active_conversation.conversation_id ? action.payload.action : state.active_conversation.archive
                };
                state.conversations = state.conversations.map(v => v.conversation_id === action.payload.conversation_id ? { ...v, archive: action.payload.action } : v);
            } else {
                state.conversations = state.conversations.map(v => v.conversation_id === action.payload.conversation_id ? { ...v, archive: action.payload.action } : v);
            }
        },
        closeConversation: (state, action) => {
            if (state.active_conversation) {
                return {
                    ...state,
                    active_conversation: {
                        ...state.active_conversation,
                        close_for: action.payload.conversation_id === state.active_conversation.conversation_id ? action.payload.action : state.active_conversation.close_for
                    },
                    conversations: state.conversations.map(v => v.conversation_id === action.payload.conversation_id ? { ...v, close_for: action.payload.action } : v),
                }
            } else {
                return {
                    ...state,
                    conversations: state.conversations.map(v => v.conversation_id === action.payload.conversation_id ? { ...v, close_for: action.payload.action } : v),
                }
            }
        },
        setActiveFile: (state, action) => {
            state.active_file = action.payload;
        },
        setActiveMsg: (state, action) => {
            state.active_msg = action.payload;
        },
        set_aiCompletedFiles: (state, action) => {
            return ({
                ...state,
                aiCompletedFiles: action.payload
            })
        },
        set_kick_out_data: (state, action) => {
            return ({
                ...state,
                kick_out_data: action.payload,
                conversations: action.payload?.kickOut_type === 'conversation' ? [...state.conversations?.filter(u => u.conversation_id !== action.payload.conversation_id)] : state.conversations
            })
        },
        set_active_conversation: (state, action) => {
            return ({
                ...state,
                active_conversation: { ...action.payload },
                msg_ai_active: false,
            })
        },
        set_raw_message_text: (state, action) => {
            return ({
                ...state,
                raw_message_text: action.payload
            })
        },
        setMuteDataOnProps: (state, action) => {
            const { data, active, conversation_id, has_mute } = action.payload;
            if (action.payload.type === 'add' || action.payload.type === 'update') {
                return {
                    ...state,
                    active_conversation: {
                        ...state.active_conversation,
                        has_mute: (active === 'yes' ? [state.user.id] : has_mute),
                        mute: (active === 'yes' ? typeof data === 'string' ? [data] : data : state.active_conversation.mute)
                    },
                    conversations: state.conversations.map((conv) => conv.conversation_id === conversation_id ? { ...conv, "mute": (active === 'yes' ? typeof data === 'string' ? [data] : data : state.active_conversation.mute), "has_mute": [state.user.id] } : conv),
                }
            } else {
                return {
                    ...state,
                    active_conversation: {
                        ...state.active_conversation,
                        has_mute: (active === 'yes' ? [] : has_mute),
                        mute: (active === 'yes' ? [] : state.active_conversation.mute)
                    },
                    conversations: state.conversations.map((conv) => (conv.conversation_id === conversation_id ? { ...conv, "mute": [], "has_mute": [] } : conv)),
                }
            }
        },
        // setCountersData: (state, action) => {
        //     state.total_unread = action.payload;
        // },
        setCountersData: (state, action) => {
            state.countersdata = action.payload;
        },
        update_CountersData: (state, action) => {
            const { taskunreadcount } = action.payload;
            return ({
                ...state,
                countersdata: { ...state.countersdata, taskunreadcount: taskunreadcount }
            })
        },
        update_total_unread: (state, action) => {
            const { taskunreadcount } = action.payload;
            return ({
                ...state,
                total_unread: { ...state.total_unread, taskunreadcount: taskunreadcount }
            })
        },
        set_online_users: (state, action) => {
            if (action.payload) {
                return {
                    ...state,
                    online_users: [...action.payload]
                }
            } else {
                return state;
            }
        },
        add_new_room: (state, action) => {
            if (action.payload.single === 'yes') {
                action.payload['title'] = action.payload.conv_name_img[state.user.id].title;
                action.payload['conv_img'] = action.payload.conv_name_img[state.user.id].img;
            }
            return ({
                ...state,
                conversations: [action.payload, ...state.conversations?.filter(u => u.conversation_id !== action.payload.conversation_id)]
            })
        },
        updateRoom: (state, action) => {
            if (!state.active_conversation) {
                return {
                    ...state,
                    conversations: state.conversations.map((v, i) =>
                        v.conversation_id === action.payload.conversation_id ?
                            {
                                ...v,
                                b_unit_id: action.payload.b_unit_id,
                                b_unit_id_name: action.payload.b_unit_id_name,
                                group_keyspace: action.payload.group_keyspace,
                                participants: action.payload.participants,
                                participants_admin: action.payload.participants_admin,
                                participants_guest: action.payload.participants_guest,
                                privacy: action.payload.privacy,
                                title: action.payload.title,
                                conv_img: action.payload.conv_img
                            }
                            : v
                    ),
                }
            } else {
                return {
                    ...state,
                    active_conversation: state.active_conversation.conversation_id === action.payload.conversation_id ?
                        {
                            ...state.active_conversation,
                            b_unit_id: action.payload.b_unit_id,
                            b_unit_id_name: action.payload.b_unit_id_name,
                            group_keyspace: action.payload.group_keyspace,
                            participants: action.payload.participants,
                            participants_admin: action.payload.participants_admin,
                            participants_guest: action.payload.participants_guest,
                            privacy: action.payload.privacy,
                            title: action.payload.title,
                            conv_img: action.payload.conv_img
                        }
                        : state.active_conversation,
                    conversations: state.conversations.map((v, i) =>
                        v.conversation_id === action.payload.conversation_id ?
                            {
                                ...v,
                                b_unit_id: action.payload.b_unit_id,
                                b_unit_id_name: action.payload.b_unit_id_name,
                                group_keyspace: action.payload.group_keyspace,
                                participants: action.payload.participants,
                                participants_admin: action.payload.participants_admin,
                                participants_guest: action.payload.participants_guest,
                                privacy: action.payload.privacy,
                                title: action.payload.title,
                                conv_img: action.payload.conv_img
                            }
                            : v
                    ),
                }
            }
        },
        sendOneMessage: (state, action) => {
            const { conversation_id, sender, mention_user, msg_body, created_at } = action.payload;
            let total_unreadLET = JSON.parse(JSON.stringify(state.total_unread));
            let conversationsLET = JSON.parse(JSON.stringify(state.conversations));
            const isUserMentioned = mention_user?.includes(state.user?.id);
            const isActiveConversation = state.active_conversation?.conversation_id === conversation_id;

            const updateConversations = (conversations) =>
                conversations.map((c) =>
                    c.conversation_id === conversation_id
                        ? {
                            ...c,
                            urmsg: sender !== state.user?.id ? (c.urmsg || 0) + 1 : c.urmsg,
                            mention_user: isUserMentioned ? 1 : c.mention_user,
                            last_msg: aesEncrypt(msg_body),
                            last_msg_time: created_at,
                        }
                        : c
                ).sort((a, b) => (a.last_msg_time > b.last_msg_time ? -1 : 1));

            const updateTotal_unread = (total_unread) => {
                const conversationExists = total_unread?.conversations?.some(
                    (c) => c.conversation_id === conversation_id
                );

                const updatedConversations = conversationExists ? total_unread.conversations.map((c) =>
                    c.conversation_id === conversation_id ? { ...c, urmsg: c.urmsg ? c.urmsg + 1 : 1 } : c
                )
                    : [...(total_unread?.conversations || []), { conversation_id, msgids: [], urmsg: 1, urreply: 0 }];

                return {
                    ...total_unread,
                    total_unread_msg: (total_unread?.total_unread_msg || 0) + 1,
                    conversations: updatedConversations,
                };
            };


            if (isActiveConversation) {
                if (state.idle_status) {
                    action.payload.msg_status = [state.user?.id];
                }

                return {
                    ...state,
                    msgs: [...state.msgs, action.payload],
                    conversations: updateConversations(state.conversations),
                };
            }

            return {
                ...state,
                total_unread: updateTotal_unread(total_unreadLET),
                conversations: updateConversations(conversationsLET),
            };
        },
        setOneReplyData: (state, action) => {
            let updatedConversations = [];
            let total_unreadClone = JSON.parse(JSON.stringify(state.total_unread));

            // Handle cases where conversations exist
            if (total_unreadClone?.conversations?.length > 0) {
                const conversationIndex = total_unreadClone.conversations.findIndex((c) => c.conversation_id === action.payload.conversation_id);
                if (conversationIndex !== -1) {
                    // Conversation exists
                    const conversation = total_unreadClone.conversations[conversationIndex];
                    const messageIndex = conversation.msgids.findIndex((m) => m.msgid === action.payload.reply_for_msgid);

                    if (messageIndex !== -1) {
                        // Message exists; update unread count
                        updatedConversations = total_unreadClone.conversations.map((c) =>
                            c.conversation_id === action.payload.conversation_id
                                ? {
                                    ...c,
                                    urreply: c.urreply + 1,
                                    msgids: c.msgids.map((m) =>
                                        m.msgid === action.payload.reply_for_msgid
                                            ? { ...m, unread: m.unread + 1 }
                                            : m
                                    ),
                                }
                                : c
                        );
                    } else {
                        // Message doesn't exist; add a new message entry
                        updatedConversations = total_unreadClone.conversations.map((c) =>
                            c.conversation_id === action.payload.conversation_id
                                ? {
                                    ...c,
                                    urreply: c.urreply + 1,
                                    msgids: [
                                        ...c.msgids,
                                        { msgid: action.payload.reply_for_msgid, unread: 1 },
                                    ],
                                }
                                : c
                        );
                    }
                } else {
                    // Conversation doesn't exist; create a new conversation
                    updatedConversations = [
                        ...total_unreadClone.conversations,
                        {
                            conversation_id: action.payload.conversation_id,
                            urmsg: 0,
                            urreply: 1,
                            msgids: [
                                {
                                    msgid: action.payload.reply_for_msgid,
                                    unread: 1,
                                },
                            ],
                        },
                    ];
                }
            } else {
                // No conversations exist; initialize first conversation
                updatedConversations = [
                    {
                        conversation_id: action.payload.conversation_id,
                        msgids: [{ msgid: action.payload.reply_for_msgid, unread: 1 }],
                        urmsg: 0,
                        urreply: 1,
                    },
                ];
            }

            // Mentioned user logic
            const isMentioned = action.payload.mention_user && action.payload.mention_user.includes(state.user?.id);

            // Update active thread
            let newState = { ...state };

            if (state.reply_main_msg && state.reply_main_msg.msg_id === action.payload.reply_for_msgid) {
                // Update active thread messages
                const msg = state.msgs.find((m) => m.msg_id === action.payload.reply_for_msgid);
                const hasReplyAttach = msg && action.payload.all_attachment.length ? (msg.has_reply_attach || 0) + 1 : msg?.has_reply_attach || 0;

                newState = {
                    ...state,
                    msgs: state.msgs.map((m) =>
                        m.msg_id === action.payload.reply_for_msgid
                            ? {
                                ...m,
                                has_reply: (m.has_reply || 0) + 1,
                                has_reply_attach: hasReplyAttach,
                                last_reply_time: action.payload.created_at,
                                last_reply_name: action.payload.sendername,
                            }
                            : m
                    ),
                    reply_msgs: action.payload.sender !== state.user.id ? [...state.reply_msgs, action.payload] : state.reply_msgs,
                };
            } else if (action.payload.conversation_id === state.active_conversation?.conversation_id) {
                // Update active conversation
                newState = {
                    ...state,
                    msgs: state.msgs.map((m) =>
                        m.msg_id === action.payload.reply_for_msgid
                            ? {
                                ...m,
                                unread_reply:
                                    action.payload.sender !== state.user?.id
                                        ? (m.unread_reply || 0) + 1
                                        : m.unread_reply,
                                has_reply: (m.has_reply || 0) + 1,
                                last_reply_time: action.payload.created_at,
                                last_reply_name: action.payload.sendername,
                            }
                            : m
                    ),
                    total_unread: {
                        ...state.total_unread,
                        total_unread_reply:
                            action.payload.sender !== state.user?.id
                                ? state.total_unread?.total_unread_reply + 1
                                : state.total_unread?.total_unread_reply,
                        conversations:
                            action.payload.sender !== state.user?.id
                                ? updatedConversations
                                : state.total_unread?.conversations,
                    },
                    conversations: state.conversations.map((conv) =>
                        conv.conversation_id === action.payload.conversation_id
                            ? {
                                ...conv,
                                r_mention_user:
                                    action.payload.sender !== state.user?.id && isMentioned
                                        ? 1
                                        : conv.r_mention_user,
                                urreply:
                                    action.payload.sender !== state.user?.id
                                        ? conv.urreply + 1
                                        : conv.urreply,
                            }
                            : conv
                    ),
                };
            } else {
                // Update other conversations
                newState = {
                    ...state,
                    total_unread: {
                        ...state.total_unread,
                        total_unread_reply:
                            action.payload.sender !== state.user?.id
                                ? state.total_unread?.total_unread_reply + 1
                                : state.total_unread?.total_unread_reply,
                        conversations:
                            action.payload.sender !== state.user?.id
                                ? updatedConversations
                                : state.total_unread?.conversations,
                    },
                    conversations: state.conversations.map((conv) =>
                        conv.conversation_id === action.payload.conversation_id
                            ? {
                                ...conv,
                                r_mention_user:
                                    action.payload.sender !== state.user?.id && isMentioned
                                        ? 1
                                        : conv.r_mention_user,
                                urreply:
                                    action.payload.sender !== state.user?.id
                                        ? conv.urreply + 1
                                        : conv.urreply,
                            }
                            : conv
                    ),
                };
            }

            return newState;
        },

        set_deleted_msg: (state, action) => {
            return {
                ...state,
                deleted_msg: action.payload,
            };
        },

        updateOneMsg: (state, action) => {
            // debugger;
            console.log("🚀 updateOneMsg ~ action:", action.payload)

            let data = [];
            let entry = false;
            let url_base_title = false;
            let updatedata = [];
            let total_unreadLET = JSON.parse(JSON.stringify(state.total_unread));

            const { field, conversation_id, msg_id, is_reply_msg, delete_type, msg_body, checklist, reply_for_msgid, updatedAt } = action.payload;

            // Determine the data and entry conditions based on the field
            switch (field) {
                case 'has_delete':
                    data = [state.user?.id];
                    if (conversation_id === state.active_conversation?.conversation_id) {
                        entry = true;
                        if (delete_type === 'for_all') {
                            data = [...state.active_conversation?.participants, 'for_all'];
                        }
                    } else {
                        updatedata = total_unreadLET.conversations.map(c =>
                            c.conversation_id === conversation_id ? { ...c, urmsg: Math.max(c.urmsg - 1, 0) } : c
                        );
                    }
                    break;

                case 'edit_history':

                    const updateConversations = (conversations) =>
                        conversations.map((c) =>
                            c.conversation_id === conversation_id
                                ? {
                                    ...c,
                                    last_msg: aesEncrypt(msg_body),
                                    last_msg_time: updatedAt,
                                }
                                : c
                        ).sort((a, b) => (a.last_msg_time > b.last_msg_time ? -1 : 1));

                    state.conversations = updateConversations(state.conversations);

                    if (conversation_id === state.active_conversation?.conversation_id) {
                        entry = true;
                        data = action.payload.edit_history;
                        url_base_title = true;
                    }

                    break;

                case 'has_emoji':
                    if (conversation_id === state.active_conversation?.conversation_id) {
                        entry = true;
                        data = action.payload.has_emoji;
                    }
                    break;

                case 'has_flagged':
                case 'has_star':
                    if (conversation_id === state.active_conversation?.conversation_id) {
                        entry = true;
                        data = action.payload.has_flagged === 'yes' ? [state.user?.id] : [];
                    }
                    break;

                case 'secret_user_name':
                case 'secret_user_details':
                case 'secret_user':
                case 'is_secret':
                    if (conversation_id === state.active_conversation?.conversation_id) {
                        entry = true;
                        data = action.payload[field];
                    }
                    break;

                case 'msg_type':
                    if (conversation_id === state.active_conversation?.conversation_id) {
                        entry = true;
                        data = 'checklist';
                    }
                    break;

                case 'full_delete':
                    if (conversation_id === state.active_conversation?.conversation_id) {
                        let { delete_type, user_id, task_id } = action.payload; //"for_me"/"for_all"
                        entry = true;
                        if (is_reply_msg === 'no') {
                            if (task_id) {
                                state.msgs = state.msgs.filter(msg => msg.msg_id !== msg_id);
                            } else {
                                state.msgs = state.msgs.map(msg => msg.msg_id === msg_id ? { ...msg, msg_body: delete_type === 'for_all' ? 'Message deleted by the sender' : 'You deleted this message', has_delete: delete_type === 'for_all' ? [user_id, 'for_all'] : [user_id] } : msg);
                            }
                            state.deleted_msg = action.payload;
                        } else {
                            state.reply_msgs = state.reply_msgs.map(msg => msg.msg_id === msg_id ? { ...msg, msg_body: delete_type === 'for_all' ? 'Message deleted by the sender' : 'You deleted this message', has_delete: delete_type === 'for_all' ? [user_id, 'for_all'] : [user_id] } : msg);
                            state.msgs = state.msgs.map(msg => msg.msg_id === reply_for_msgid ? { ...msg, has_reply: msg.has_reply > 0 ? Number(msg.has_reply) - 1 : 0 } : msg);
                        }
                    } else {
                        updatedata = total_unreadLET.conversations.map(c =>
                            c.conversation_id === conversation_id ? { ...c, urmsg: Math.max(c.urmsg - 1, 0) } : c
                        );
                    }
                    break;

                default:
                    break;
            }

            // Apply updates if entry is true
            if (entry) {
                const updateMessages = (messages) =>
                    messages.map(msg =>
                        msg.msg_id === msg_id
                            ? {
                                ...msg,
                                [field]: data,
                                msg_body: msg_body !== undefined ? msg_body : msg.msg_body,
                                checklist: data === 'checklist' ? checklist : msg.checklist,
                            }
                            : msg
                    );

                if (is_reply_msg === 'yes') {
                    if (url_base_title) {
                        state.reply_msgs = state.reply_msgs.map(msg =>
                            msg.msg_id === msg_id ? { ...action.payload.msg } : msg
                        );
                    } else {
                        state.reply_msgs = updateMessages(state.reply_msgs);
                    }
                } else {
                    if (url_base_title) {
                        state.msgs = state.msgs.map(msg =>
                            msg.msg_id === msg_id ? { ...action.payload.msg } : msg
                        );


                    } else {
                        state.msgs = updateMessages(state.msgs);
                    }
                }
            } else if (updatedata.length) {
                state.total_unread = {
                    ...state.total_unread,
                    total_unread_msg: state.total_unread?.total_unread_msg - 1,
                    conversations: updatedata,
                };
            }
        },
        removeAttachment: (state, action) => {
            if (state.active_conversation) {
                if (state.active_conversation.conversation_id === action.payload.conversation_id) {
                    return {
                        ...state,
                        msgs: state.msgs.map(v => v.msg_id === action.payload.msg_id ? { ...v, all_attachment: v.all_attachment.filter(f => f.id !== action.payload.file_id) } : v),
                        change_file_data: action.payload
                    }
                } else {
                    return {
                        ...state,
                        change_file_data: action.payload
                    }
                }
            } else {
                return {
                    ...state,
                    change_file_data: action.payload
                }
            }
        },
        file_share_link_create: (state, action) => {
            if (state.active_conversation) {
                if (state.active_conversation.conversation_id === action.payload.conversation_id) {
                    return {
                        ...state,
                        msgs: state.msgs.map(v => v.msg_id === action.payload.msg_id ? { ...v, all_attachment: v.all_attachment.map(f => f.id === action.payload.id ? { ...f, url_short_id: action.payload.url_short_id } : f) } : v),
                        // active_thread: state.active_thread && state.active_thread?.msg_id === action.payload.msg_id ? { ...state.active_thread, all_attachment: state.active_thread?.all_attachment.map(f => f.id === action.payload.id ? { ...f, url_short_id: action.payload.url_short_id } : f) } : state.active_thread,
                        reply_msgs: state.reply_msgs.map(v => v.msg_id === action.payload.msg_id ? { ...v, all_attachment: v.all_attachment.map(f => f.id === action.payload.id ? { ...f, url_short_id: action.payload.url_short_id } : f) } : v),
                        change_file_data: action.payload
                    }
                } else {
                    return {
                        ...state,
                        change_file_data: action.payload
                    }
                }
            } else {
                return {
                    ...state,
                    change_file_data: action.payload
                }
            }
        },

        updateMsgTag: (state, action) => {
            const { payload } = action;
            const {
                conversation_id,
                msg_id,
                file_id,
                is_reply,
                removetag,
                newtag,
                newtag_tag_data,
            } = payload;

            const updateAttachments = (attachments) =>
                attachments.map((f) =>
                    f.id === file_id
                        ? {
                            ...f,
                            tag_list_details: [
                                ...(f.tag_list_details || []).filter((t) => !removetag.includes(t.tag_id)),
                                ...newtag_tag_data.filter((t) => !f.tag_list.includes(t.tag_id)),
                            ],
                            tag_list: [
                                ...f.tag_list.filter((t) => !removetag.includes(t)),
                                ...newtag.filter((t) => !f.tag_list.includes(t)),
                            ],
                        }
                        : f
                );

            const updateMessages = (messages) =>
                messages.map((v) =>
                    v.msg_id === msg_id
                        ? {
                            ...v,
                            all_attachment: updateAttachments(v.all_attachment),
                        }
                        : v
                );

            if (state.active_conversation) {
                if (conversation_id === state.active_conversation.conversation_id) {
                    return {
                        ...state,
                        msgs: updateMessages(state.msgs),
                        reply_msgs: is_reply === 'yes' ? updateMessages(state.reply_msgs) : state.reply_msgs,
                        change_file_data: payload,
                    };
                }
            }

            return {
                ...state,
                change_file_data: payload,
            };
        },
        updateMessageStar: (state, action) => {
            if (state.active_conversation) {
                if (action.payload.is_reply_msg === 'yes') {
                    let reply_msgs = state.reply_msgs.map(v =>
                        v.msg_id === action.payload.msg_id ?
                            {
                                ...v,
                                all_attachment: v.all_attachment.map(f => f.id === action.payload.file_id ? { ...f, star: action.payload.star } : f)
                            }
                            : v
                    );
                    return {
                        ...state,
                        reply_msgs: reply_msgs,
                        change_file_data: action.payload
                    }
                } else {
                    return {
                        ...state,
                        msgs: state.msgs.map(v =>
                            v.msg_id === action.payload.msg_id ?
                                {
                                    ...v,
                                    all_attachment: v.all_attachment.map(f => f.id === action.payload.file_id ? { ...f, star: action.payload.star } : f)
                                }
                                : v
                        ),
                        change_file_data: action.payload
                    }
                }
            } else {
                return {
                    ...state,
                    change_file_data: action.payload
                }
            }
        },
        updateFlagCounter: (state, action) => {
            if (state.total_unread) {
                state.total_unread.all_flag_conversation = action.payload.all_flag_conversation
            } else {
                console.log('total_unread:', state.total_unread);
            }
        },
        updateMsgCounter: (state, action) => {
            const { is_reply_msg, read, conversation_id, root_msg_id } = action.payload;

            if (!state.total_unread) return;

            const updatedConversations = state.total_unread?.conversations?.map((v) => {
                if (v.conversation_id !== conversation_id) return v;

                if (is_reply_msg === 'yes') {
                    return {
                        ...v,
                        msgids: v.msgids.map((m) => {
                            if (m.msgid === root_msg_id) {
                                return { ...m, unread: Math.max(m.unread - read, 0) };
                            } else if (root_msg_id === "") {
                                return { ...m, unread: 0 };
                            }
                            return m;
                        }),
                        urreply: Math.max(v.urreply - read, 0),
                    };
                } else {
                    return {
                        ...v,
                        urmsg: Math.max(v.urmsg - read, 0),
                    };
                }
            });

            state.total_unread = {
                ...state.total_unread,
                conversations: updatedConversations,
                ...(is_reply_msg === 'yes'
                    ? {
                        total_unread_reply: Math.max(state.total_unread?.total_unread_reply - read, 0),
                    }
                    : {
                        total_unread_msg: Math.max(state.total_unread?.total_unread_msg - read, 0),
                    }),
            };
        },
        setMergeMsg: (state, action) => {
            return {
                ...state,
                merge_msg: action.payload,
            }
        },
        set_change_link_data: (state, action) => {
            return {
                ...state,
                change_link_data: action.payload,
            }
        },
        set_callringpopup: (state, action) => {
            // debugger;
            return {
                ...state,
                callringpopup: action.payload
            }
        },
        setConvShortUrl: (state, action) => {
            // debugger
            if (state.active_conversation) {
                return {
                    ...state,
                    active_conversation: {
                        ...state.active_conversation,
                        short_id: action.payload?.short_id || ""
                    },
                }
            }
        },
        set_conv_calling_data: (state, action) => {
            // debugger
            return {
                ...state,
                conv_calling_data: action.payload
            }
        },
        setUpdateVersion: (state, action) => {
            return {
                ...state,
                updateVersion_data: action.payload
            }
        },
    },
});

export const {
    setPopup, setUser, update_user_erp, setAllUsers, setConversations, add_conversations, setConversationMap, updateConversationMap, updateConversationMapKey, setActiveConversation, set_messages, update_messages,
    removeMessage, set_pin_unpin, updateArchiveConv, closeConversation, setActiveFile, setActiveMsg, set_aiCompletedFiles, update_msg_link, set_reply_main_msg,
    set_archive_conversations, set_kick_out_data, set_active_conversation, set_raw_message_text, update_room_data, setMuteDataOnProps, update_total_unread, set_online_users,
    updateOneMsg, set_deleted_msg, setOneReplyData, sendOneMessage, updateRoom, add_new_room, updateMessageStar, updateMsgTag, file_share_link_create, removeAttachment,
    updateMsgCounter, updateFlagCounter, setMergeMsg, set_change_link_data, set_callringpopup, setConvShortUrl, set_conv_calling_data, setCountersData,
    update_CountersData, setUpdateVersion
} = messageSlice.actions;
export default messageSlice.reducer;
